/* =========================================================
   UMPROTECH — PRODUCTION KILLER CUSTOMER-ADDRESSES.JS V4
   Dawn-compatible customer address manager
   Scope: customer addresses page only
   Safe rules:
   - no form action changes
   - no customer data changes
   - no URL changes
   - safe Shopify.CountryProvinceSelector guard
   ========================================================= */

const selectors = Object.freeze({
  customerAddresses: '[data-customer-addresses]',
  addressCountrySelect: '[data-address-country-select]',
  addressContainer: '[data-address]',
  toggleAddressButton: 'button[aria-expanded]',
  cancelAddressButton: 'button[type="reset"]',
  deleteAddressButton: 'button[data-confirm-message]',
});

const attributes = Object.freeze({
  expanded: 'aria-expanded',
  confirmMessage: 'data-confirm-message',
});

class CustomerAddresses {
  constructor() {
    this.elements = this._getElements();

    if (!this.elements.container) return;

    this._setupCountries();
    this._setupEventListeners();
  }

  _getElements() {
    const container = document.querySelector(selectors.customerAddresses);

    if (!container) return {};

    return {
      container,
      addressContainers: container.querySelectorAll(selectors.addressContainer),
      toggleButtons: container.querySelectorAll(selectors.toggleAddressButton),
      cancelButtons: container.querySelectorAll(selectors.cancelAddressButton),
      deleteButtons: container.querySelectorAll(selectors.deleteAddressButton),
      countrySelects: container.querySelectorAll(selectors.addressCountrySelect),
    };
  }

  _setupCountries() {
    const hasCountryProvinceSelector =
      window.Shopify &&
      typeof window.Shopify.CountryProvinceSelector === 'function';

    if (!hasCountryProvinceSelector) return;

    const newCountry = document.getElementById('AddressCountryNew');
    const newProvince = document.getElementById('AddressProvinceNew');
    const newProvinceContainer = document.getElementById('AddressProvinceContainerNew');

    if (newCountry && newProvince && newProvinceContainer) {
      // eslint-disable-next-line no-new
      new window.Shopify.CountryProvinceSelector('AddressCountryNew', 'AddressProvinceNew', {
        hideElement: 'AddressProvinceContainerNew',
      });
    }

    this.elements.countrySelects.forEach((select) => {
      if (!select || !select.dataset || !select.dataset.formId) return;

      const formId = select.dataset.formId;
      const countryId = `AddressCountry_${formId}`;
      const provinceId = `AddressProvince_${formId}`;
      const provinceContainerId = `AddressProvinceContainer_${formId}`;

      if (
        !document.getElementById(countryId) ||
        !document.getElementById(provinceId) ||
        !document.getElementById(provinceContainerId)
      ) {
        return;
      }

      // eslint-disable-next-line no-new
      new window.Shopify.CountryProvinceSelector(countryId, provinceId, {
        hideElement: provinceContainerId,
      });
    });
  }

  _setupEventListeners() {
    this.elements.toggleButtons.forEach((button) => {
      button.addEventListener('click', this._handleAddEditButtonClick);
    });

    this.elements.cancelButtons.forEach((button) => {
      button.addEventListener('click', this._handleCancelButtonClick);
    });

    this.elements.deleteButtons.forEach((button) => {
      button.addEventListener('click', this._handleDeleteButtonClick);
    });
  }

  _toggleExpanded(target) {
    if (!target) return;

    const isExpanded = target.getAttribute(attributes.expanded) === 'true';
    target.setAttribute(attributes.expanded, String(!isExpanded));
  }

  _handleAddEditButtonClick = (event) => {
    const { currentTarget } = event;
    this._toggleExpanded(currentTarget);
  };

  _handleCancelButtonClick = (event) => {
    const { currentTarget } = event;
    const addressContainer = currentTarget.closest(selectors.addressContainer);

    if (!addressContainer) return;

    const toggleButton = addressContainer.querySelector(`[${attributes.expanded}]`);
    this._toggleExpanded(toggleButton);
  };

  _handleDeleteButtonClick = (event) => {
    const { currentTarget } = event;

    if (!currentTarget) return;

    const confirmMessage = currentTarget.getAttribute(attributes.confirmMessage);
    const target = currentTarget.dataset ? currentTarget.dataset.target : null;

    if (!confirmMessage || !target) return;

    // eslint-disable-next-line no-alert
    const confirmed = window.confirm(confirmMessage);

    if (!confirmed) return;

    if (!window.Shopify || typeof window.Shopify.postLink !== 'function') {
      window.location.href = target;
      return;
    }

    window.Shopify.postLink(target, {
      parameters: { _method: 'delete' },
    });
  };
}

/* Prevent duplicate initialization if theme editor reloads scripts */
if (!window.UmproTechCustomerAddressesInitialized) {
  window.UmproTechCustomerAddressesInitialized = true;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      new CustomerAddresses();
    });
  } else {
    new CustomerAddresses();
  }
}
